
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSeoMetadataForPage } from './useSeoMetadata';

export const useDynamicSeo = () => {
  const location = useLocation();
  const { metadata, loading } = useSeoMetadataForPage(location.pathname);

  useEffect(() => {
    console.log('Dynamic SEO for path:', location.pathname);
    console.log('SEO metadata found:', metadata);
    console.log('Loading:', loading);

    if (metadata && !loading && typeof document !== 'undefined') {
      // Update title dynamically
      document.title = metadata.title;
      
      // Update meta title (if exists)
      let metaTitle = document.head.querySelector('meta[name="title"]');
      if (metaTitle) {
        metaTitle.setAttribute('content', metadata.title);
      } else {
        metaTitle = document.createElement('meta');
        metaTitle.setAttribute('name', 'title');
        metaTitle.setAttribute('content', metadata.title);
        document.head.appendChild(metaTitle);
      }
      
      // Update meta description
      let metaDescription = document.head.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', metadata.meta_description);

      // Update canonical URL - prioritize canonical_url from database if set
      let canonicalLink = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      const canonicalHref = metadata.canonical_url || `https://maisoneridia.fr${metadata.page_path}`;
      canonicalLink.setAttribute('href', canonicalHref);

      // Update Open Graph title
      let ogTitle = document.head.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', metadata.title);

      // Update Open Graph description
      let ogDescription = document.head.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', metadata.meta_description);

      // Update Open Graph URL
      let ogUrl = document.head.querySelector('meta[property="og:url"]');
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute('content', `https://maisoneridia.fr${metadata.page_path}`);

      // Update Open Graph image
      if (metadata.og_image_url) {
        let ogImage = document.head.querySelector('meta[property="og:image"]');
        if (!ogImage) {
          ogImage = document.createElement('meta');
          ogImage.setAttribute('property', 'og:image');
          document.head.appendChild(ogImage);
        }
        ogImage.setAttribute('content', metadata.og_image_url);
      }

      // Update Twitter title
      let twitterTitle = document.head.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.setAttribute('name', 'twitter:title');
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.setAttribute('content', metadata.title);

      // Update Twitter description
      let twitterDescription = document.head.querySelector('meta[name="twitter:description"]');
      if (!twitterDescription) {
        twitterDescription = document.createElement('meta');
        twitterDescription.setAttribute('name', 'twitter:description');
        document.head.appendChild(twitterDescription);
      }
      twitterDescription.setAttribute('content', metadata.meta_description);

      // Update Twitter image
      if (metadata.og_image_url) {
        let twitterImage = document.head.querySelector('meta[name="twitter:image"]');
        if (!twitterImage) {
          twitterImage = document.createElement('meta');
          twitterImage.setAttribute('name', 'twitter:image');
          document.head.appendChild(twitterImage);
        }
        twitterImage.setAttribute('content', metadata.og_image_url);
      }

      // Update keywords
      if (metadata.keywords) {
        let metaKeywords = document.head.querySelector('meta[name="keywords"]');
        if (!metaKeywords) {
          metaKeywords = document.createElement('meta');
          metaKeywords.setAttribute('name', 'keywords');
          document.head.appendChild(metaKeywords);
        }
        metaKeywords.setAttribute('content', metadata.keywords);
      }

      // Update robots meta
      let robotsMeta = document.head.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', metadata.no_index ? 'noindex, nofollow' : 'index, follow');
    }
  }, [location.pathname, metadata, loading]);

  // Écouter les changements en temps réel
  useEffect(() => {
    const handleRealtimeUpdate = (event: CustomEvent) => {
      console.log('SEO realtime update received:', event.detail);
    };

    window.addEventListener('seo-realtime-update', handleRealtimeUpdate as EventListener);
    
    return () => {
      window.removeEventListener('seo-realtime-update', handleRealtimeUpdate as EventListener);
    };
  }, []);

  return { metadata, loading };
};
